import { LAST_JACKPOT_GAME_DETAILS_QUERY, useGetUserProfileByAddress } from '@/api/query'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { Spinner } from '@/components/atoms/Spinner'
import { EXPLORERS, JACKPOT_FEE, SHU_DENOM, USDC_NEW_DENOM } from '@/constants/constants'
import { GameStage, Player } from '@/context/JackpotProvider'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Center,
  Flex,
  Box,
  SimpleGrid,
  useMediaQuery,
  Button,
} from '@chakra-ui/react'
import { GameParticipants } from '../GameParticipants/GameParticipants'
import { getGroupedPlayers } from '@/helpers/getGroupedPlayers'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { useTranslation } from 'next-i18next/pages'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { useQuery as useApolloQuery } from '@apollo/client'
import { GetJackpotGameDetailsQuery } from '@/__generated__/graphql'
import ShareIcon from '@/public/assets/icons/share.svg'
import { Game } from 'types/types'
import { BackButton } from '@/components/atoms/BackButton'
import { ShareResultContent } from '../ShareResultModal/ShareResultModal'
import { useState } from 'react'
import Link from 'next/link'
import { TokenMoney } from '@ninjablaze/sdk-ts'

type JackpotGameDetails = {
  id: string
  players: Array<Player>
  pool: string
  start_block: number
  created: Date
  state: GameStage
  player_winner: string | null
  win_amount: string
  randomness: string
}

enum Steps {
  DETAILS,
  SHARING,
}

export default ({ onClose, gameId }: { onClose: () => void; gameId: string }) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(Steps.DETAILS)
  const { data } = useApolloQuery(LAST_JACKPOT_GAME_DETAILS_QUERY, {
    variables: { id: gameId || '' },
  })

  const details = transformJackpotDetailsGame(data) as any

  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          <Flex gap={2} alignItems={'center'}>
            {step === Steps.SHARING && (
              <BackButton callback={() => setStep(Steps.DETAILS)} styles={{ h: '30px', w: '30px', p: 1.5 }} />
            )}
            {t('common.game-details', { gameId })}
          </Flex>
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          {step === Steps.DETAILS && <GameDetailsContent switchTab={() => setStep(Steps.SHARING)} data={details} />}
          {step === Steps.SHARING && <ShareResultContent game={Game.JACKPOT} gameId={gameId} />}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const transformJackpotDetailsGame = (data: GetJackpotGameDetailsQuery | undefined) => {
  if (!data) {
    return null
  }

  const game = data.jackpotGame

  return {
    players: getGroupedPlayers(
      game!.jackpotBetsByGameId!.nodes!.map((bet) => ({ address: bet!.address, amount: bet!.amount }) as any),
      game!.pool
    ),
    id: game!.id,
    randomness: game!.randomness,
    state: game!.state,
    start_block: game!.startBlock,
    created: new Date(game!.created),
    win_amount: game!.winAmount,
    pool: game!.pool,
    player_winner: game!.playerWinner,
  }
}

const GameDetailsContent = ({ data, switchTab }: { data: JackpotGameDetails | null; switchTab: () => void }) => {
  const { t } = useTranslation()
  const { data: userResponse } = useGetUserProfileByAddress(data?.player_winner || '')
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)')

  const numberOfNfts = userResponse?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)

  if (!data || !userResponse) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  const winner = getGroupedPlayers(data.players, data.pool).find((player) => player.address === data.player_winner)!

  return (
    <>
      <Flex gap={{ base: 4, md: 8 }} mb={3} flexDir={{ base: 'column', md: 'row' }}>
        <Flex alignItems={'center'} justifyContent={{ base: 'center', md: 'start' }} gap={3} rounded={'xl'}>
          <AvatarCircle size={'lg'} offset={4} src={userResponse.user?.profileAvatar || ''} />
          <Flex flexDir={'column'} alignItems={'start'}>
            {isNftOwner ? (
              <GradientText
                numberOfNfts={numberOfNfts!}
                props={{
                  fontWeight: 700,
                  fontSize: 'xl',
                }}
              >
                {userResponse?.user?.profileUserName}
              </GradientText>
            ) : (
              <Text fontWeight={700} fontSize={'xl'}>
                {userResponse?.user?.profileUserName}
              </Text>
            )}
            <Box rounded={'lg'} bg={'#2EB47C'} fontSize={'xs'} fontWeight={700} py={0.5} px={2.5}>
              {t('Jackpot.winner')}
            </Box>
          </Flex>
        </Flex>
        <Box flexBasis={'full'} rounded={'3xl'} border={'2px solid'} py={4} px={6} borderColor={'ninja.500'}>
          <Text fontSize={'sm'} color={'ninja.200'} fontWeight={700}>
            {t('Statistics.winners-prize')}
          </Text>
          <FiatOrCryptoAmount
            value={new TokenMoney(data.pool, Number(data.id) <= 7655 ? USDC_NEW_DENOM : SHU_DENOM)}
            iconSize={'20px'}
            wrapperStyles={{
              fontSize: 'xl',
            }}
          />
        </Box>
      </Flex>
      <SimpleGrid columnGap={3} columns={2}>
        <StatsRow
          name={t('common.bet')}
          value={
            <FiatOrCryptoAmount
              iconSize={'18px'}
              value={new TokenMoney(winner?.amount, Number(data.id) <= 7655 ? USDC_NEW_DENOM : SHU_DENOM)}
            />
          }
        />
        <StatsRow name={t('common.chance')} value={<Text color={'brand.green'}>{winner?.chance}%</Text>} />
      </SimpleGrid>
      <Flex mb={6} flexDir={'column'} alignItems={'center'}>
        <Text mb={1.5} color={'ninja.200'} fontSize={'sm'} fontWeight={700}>
          {t('common.participants')}
        </Text>
        <GameParticipants
          maxLength={7}
          players={data.players.map((player) => player.address)}
          winner={data.player_winner!}
        />
      </Flex>
      <SimpleGrid columnGap={3} columns={2}>
        <StatsRow
          name={t('common.game-status')}
          value={
            <Text as={'span'} textTransform={'capitalize'} color={'brand.green'}>
              {data.state}
            </Text>
          }
        />
        <StatsRow
          name={t('common.date')}
          value={<Text fontSize={{ base: 'xs', md: 'sm' }}>{data.created.toLocaleString()}</Text>}
        />
        <StatsRow
          name={t('common.payout')}
          value={
            <FiatOrCryptoAmount
              iconSize={'18px'}
              value={new TokenMoney(data.win_amount, Number(data.id) <= 7655 ? USDC_NEW_DENOM : SHU_DENOM)}
            />
          }
        />
        <StatsRow name={t('common.fee')} value={(JACKPOT_FEE * 100).toString() + '%'} />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 2 }} columnGap={3}>
        <StatsRow
          name={t('Jackpot.start-block')}
          value={
            <CopiableLink
              value={data.start_block.toString()}
              link={`${EXPLORERS.INJECTIVE_BLOCKS}${data.start_block}`}
            />
          }
        />
        <StatsRow
          name={t('common.randomness')}
          value={<CopiableLink trimmedLength={isLargerThanMd ? 16 : 24} value={data.randomness} />}
        />
      </SimpleGrid>
      <SimpleGrid columns={2} gap={3} mt={1}>
        <Button variant={'orange'} rounded={'xl'} gap={2.5} onClick={switchTab}>
          Share <ShareIcon />
        </Button>
        <Link href={'/jackpot/history/' + data.id}>
          <Button w={'full'} rounded={'xl'}>
            More details
          </Button>
        </Link>
      </SimpleGrid>
    </>
  )
}
